/**
 * Facts about one thing, on one line — "12 May – 3 Jul · 52 days · Brazil".
 * Each fact is its own item and the dot between them is a decoration (hidden
 * from screen readers, which hear a list): no sentence is glued together in
 * code, so every fact is translated whole and a fact that is absent leaves no
 * stray separator.
 *
 * Presentation only: the facts are the caller's, translated.
 */
import { Children, isValidElement, type ReactNode } from 'react';

import { cn } from '../lib/utils';

/** The dot between facts — typography, not words. */
const SEPARATOR = '·';

export function MetaList({
  children,
  className,
  'data-testid': testId,
}: {
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  // `toArray` already drops null, undefined and booleans — an absent fact.
  const facts = Children.toArray(children).filter((child) => child !== '');
  return (
    <ul className={cn('flex flex-wrap items-baseline gap-x-1.5', className)} data-testid={testId}>
      {facts.map((fact, i) => (
        <li key={isValidElement(fact) && fact.key !== null ? fact.key : i}>
          {i > 0 ? (
            <span aria-hidden="true" className="mr-1.5 text-muted-foreground">
              {SEPARATOR}
            </span>
          ) : null}
          {fact}
        </li>
      ))}
    </ul>
  );
}
